/**
 * Unified Parent Layout Component - Parent Portal Refactor
 * 
 * CREATED FOR: Parent Portal refactor & redesign (Phase 2.1)
 * PURPOSE: Redesign ParentLayout with unified responsive navigation
 * TECH DEBT: Replaces complex mobile/desktop layout switching logic
 * 
 * This component addresses critical tech debt by:
 * - Using the new ParentSidebarUnified component
 * - Implementing proper mobile-first responsive design
 * - Adding consistent page header system across all portal pages
 * - Using new responsive design tokens for spacing and layout
 * - Eliminating duplicate sidebar logic
 * 
 * Key improvements:
 * - Single navigation component handles all device types
 * - Proper responsive spacing using design tokens
 * - Enhanced mobile interaction patterns
 * - Consistent breadcrumb navigation system
 * - Better accessibility and touch targets
 * - Unified animation and transition patterns
 */

import React, { Suspense, useState } from "react";
import { Toaster } from "sonner";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { useOnlineStatus } from "@/utils/connectionUtils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { 
  WifiOff, 
  Menu, 
  ChevronLeft, 
  Home,
  ArrowLeft
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useResponsive } from "@/hooks/useResponsive";
import { useLocation, useNavigate, Link } from "react-router-dom";
import ParentSidebarUnified from "./ParentSidebarUnified";
import TopBar from "./TopBar";
import { 
  RESPONSIVE_SPACING, 
  RESPONSIVE_TYPOGRAPHY, 
  RESPONSIVE_CONTAINERS,
  TOUCH_TARGETS 
} from "@/design-tokens/responsive";

interface ParentLayoutUnifiedProps {
  children: React.ReactNode;
  hideTopBar?: boolean;
  pageTitle?: string;
  pageDescription?: string;
  showBackButton?: boolean;
  backButtonHref?: string;
  className?: string;
  organizationLogo?: string;
  organizationName?: string;
  rightContent?: React.ReactNode;
  hideHomeButton?: boolean;
}

/**
 * Enhanced page header component with responsive design and breadcrumb navigation
 */
const PageHeader: React.FC<{
  title?: string;
  description?: string;
  showBackButton?: boolean;
  backButtonHref?: string;
  isMobile: boolean;
  organizationLogo?: string;
  organizationName?: string;
  rightContent?: React.ReactNode;
  hideHomeButton?: boolean;
}> = ({ title, description, showBackButton, backButtonHref, isMobile, organizationLogo, organizationName, rightContent, hideHomeButton }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-generate breadcrumb if not provided
  const generateBreadcrumb = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length <= 2) return null; // No breadcrumb for main pages
    
    const parentPath = pathSegments.slice(0, -1).join('/');
    return `/${parentPath}`;
  };

  const finalBackHref = backButtonHref || generateBreadcrumb();

  const handleBackClick = () => {
    if (finalBackHref) {
      navigate(finalBackHref);
    } else {
      navigate(-1);
    }
  };

  if (!title && !organizationLogo && !showBackButton && !rightContent) return null;

  return (
    <div className={cn(
      "border-b border-gray-200/60 bg-white/80 backdrop-blur-sm",
      RESPONSIVE_SPACING.container.mobile,
      RESPONSIVE_SPACING.container.tablet,
      "py-4 md:py-6"
    )}>
      <div className="flex items-center gap-3">
        {(showBackButton || finalBackHref) && (
          <Button
            variant="ghost"
            size={isMobile ? "sm" : "default"}
            onClick={handleBackClick}
            className={cn(
              "rounded-xl transition-colors",
              isMobile ? "h-9 w-9 p-0" : "h-10 px-3"
            )}
            style={{ minHeight: TOUCH_TARGETS.minSize, minWidth: TOUCH_TARGETS.minSize }}
          >
            <ArrowLeft className="h-4 w-4" />
            {!isMobile && <span className="ml-2 text-sm font-medium">Back</span>}
            <span className="sr-only">Go back</span>
          </Button>
        )}

        <div className="flex-1 min-w-0">
          {organizationLogo ? (
            <div className="flex items-center">
              <img 
                src={organizationLogo} 
                alt={organizationName || "Organization Logo"} 
                className={cn(
                  "object-contain",
                  isMobile ? "h-8 w-auto max-w-32" : "h-10 w-auto max-w-40"
                )}
                onError={(e) => {
                  // Fallback to title if logo fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallbackTitle = document.createElement('h1');
                  fallbackTitle.className = 'font-bold text-gray-900 tracking-tight text-lg md:text-xl';
                  fallbackTitle.textContent = title || 'Dashboard';
                  target.parentElement!.appendChild(fallbackTitle);
                }}
              />
            </div>
          ) : title ? (
            <h1 className={cn(
              "font-bold text-gray-900 tracking-tight",
              RESPONSIVE_TYPOGRAPHY.h2
            )}>
              {title}
            </h1>
          ) : null}
          {description && (
            <p className={cn(
              "text-gray-600 mt-1",
              RESPONSIVE_TYPOGRAPHY.cardSubtitle
            )}>
              {description}
            </p>
          )}
        </div>

        {/* Right content (e.g., cart button, search, etc.) */}
        {rightContent && (
          <div className="flex items-center gap-2">
            {rightContent}
          </div>
        )}

        {/* Quick action: Home button for mobile */}
        {isMobile && !rightContent && !hideHomeButton && (
          <Link to="/parent-portal">
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 rounded-xl"
              style={{ minHeight: TOUCH_TARGETS.minSize, minWidth: TOUCH_TARGETS.minSize }}
            >
              <Home className="h-4 w-4" />
              <span className="sr-only">Go to dashboard</span>
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

/**
 * Loading skeleton component for better perceived performance
 */
const LoadingSkeleton: React.FC = () => (
  <div className={cn(
    "animate-pulse space-y-4",
    RESPONSIVE_SPACING.container.mobile,
    RESPONSIVE_SPACING.container.tablet
  )}>
    <div className="h-8 bg-gray-200 rounded-lg w-1/3"></div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded w-full"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      <div className="h-4 bg-gray-200 rounded w-4/6"></div>
    </div>
  </div>
);

const ParentLayoutUnified: React.FC<ParentLayoutUnifiedProps> = ({ 
  children,
  hideTopBar = false,
  pageTitle,
  pageDescription,
  showBackButton = false,
  backButtonHref,
  className,
  organizationLogo,
  organizationName,
  rightContent,
  hideHomeButton = false
}) => {
  const isOnline = useOnlineStatus();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className={cn(
      RESPONSIVE_CONTAINERS.page,
      "bg-gradient-to-br from-slate-50 via-white to-blue-50"
    )}>
      {/* Unified Sidebar - handles its own responsive behavior */}
      {isDesktop ? (
        // Desktop: Always visible sidebar
        <div className="fixed left-0 top-0 z-30">
          <ParentSidebarUnified 
            isOpen={true}
            onToggle={toggleSidebar}
            onClose={closeSidebar}
          />
        </div>
      ) : (
        // Mobile/Tablet: Overlay sidebar
        <ParentSidebarUnified 
          isOpen={sidebarOpen}
          onToggle={toggleSidebar}
          onClose={closeSidebar}
        />
      )}
      
      {/* Main content area with proper responsive margins */}
      <div className={cn(
        "flex flex-col min-h-screen transition-all duration-300",
        // Desktop: Account for fixed sidebar
        isDesktop && "ml-72 lg:ml-80",
        // Mobile/Tablet: Full width
        (isMobile || isTablet) && "w-full"
      )}>
        {/* Enhanced Top bar with responsive design */}
        {!hideTopBar && (
          <div className={cn(
            RESPONSIVE_CONTAINERS.nav,
            "h-14 md:h-16"
          )}>
            <div className={cn(
              "relative flex items-center h-full",
              RESPONSIVE_SPACING.container.mobile,
              RESPONSIVE_SPACING.container.tablet
            )}>
              {/* Mobile hamburger menu */}
              {(isMobile || isTablet) && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="mr-3 rounded-xl"
                  onClick={toggleSidebar}
                  style={{ minHeight: TOUCH_TARGETS.minSize, minWidth: TOUCH_TARGETS.minSize }}
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle Menu</span>
                </Button>
              )}
              
              <div className="flex-1">
                <TopBar 
                  sidebarId="parent-sidebar-unified" 
                  hideMenuButton={true} 
                  onGettingStartedClick={() => setOnboardingOpen(true)} 
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Page Header - consistent across all pages */}
        <PageHeader
          title={pageTitle}
          description={pageDescription}
          showBackButton={showBackButton}
          backButtonHref={backButtonHref}
          isMobile={isMobile}
          organizationLogo={organizationLogo}
          organizationName={organizationName}
          rightContent={rightContent}
          hideHomeButton={hideHomeButton}
        />
        
        {/* Main content with responsive padding and proper container */}
        <main className={cn(
          "flex-1",
          RESPONSIVE_CONTAINERS.content,
          className
        )}>
          {/* Offline notification */}
          {!isOnline && (
            <Alert className={cn(
              "mb-6 bg-amber-50 border-amber-300 rounded-xl",
              RESPONSIVE_SPACING.container.mobile
            )}>
              <WifiOff className="h-4 w-4 text-amber-800" />
              <AlertDescription className="text-amber-800 font-medium">
                You are currently offline. Some features may be unavailable.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Content with error boundary */}
          <ErrorBoundary componentName="ParentPortalContent">
            <Suspense fallback={<LoadingSkeleton />}>
              {children}
            </Suspense>
          </ErrorBoundary>
        </main>
        
        {/* Mobile-friendly footer spacer */}
        {isMobile && <div className="h-6" />}
      </div>

      {/* Toaster for notifications */}
      <Toaster 
        position={isMobile ? "top-center" : "bottom-right"}
        toastOptions={{
          style: {
            minHeight: TOUCH_TARGETS.minSize,
          }
        }}
      />
      
      {/* Onboarding modal placeholder */}
      {onboardingOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setOnboardingOpen(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Getting Started</h2>
            <p className="text-gray-600 mb-4">Welcome to the Parent Portal!</p>
            <Button 
              onClick={() => setOnboardingOpen(false)}
              className="w-full"
              style={{ minHeight: TOUCH_TARGETS.minSize }}
            >
              Got it!
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentLayoutUnified;